import { CollectionEntity, Timestamp } from "@busy-human/gearbox";
import { BuilderRecord } from "../BuilderRecord/index.js";
import { BuilderHealthRecord } from "../BuilderHealthRecord/index.js";
import { Trips } from "../Trips/index.js";
import { ChecklistDefinitions } from "../ChecklistDefinitions/index.js";
import { ChecklistOverrides } from "../ChecklistOverrides/index.js";
import { ParentTrainings } from "../ParentTrainings/index.js";
const RE_SEASON_YEAR = /^[0-9]{4}$/i;
export var Season;
(function (Season) {
    let Features;
    (function (Features) {
        Features["BUILDERS"] = "BUILDERS";
        Features["TRIP_SEARCH"] = "TRIP_SEARCH";
        Features["CART"] = "CART";
        Features["REGISTRATION"] = "REGISTRATION";
        Features["PAYMENT"] = "PAYMENT";
        Features["DOCUMENTS"] = "DOCUMENTS";
        Features["CHECKLIST"] = "CHECKLIST";
        Features["CHANGE_TRIP"] = "CHANGE_TRIP";
    })(Features = Season.Features || (Season.Features = {}));
    ;
    let FeatureState;
    (function (FeatureState) {
        FeatureState["ENABLED"] = "ENABLED";
        FeatureState["DISABLED"] = "DISABLED";
        FeatureState["READ_ONLY"] = "READ_ONLY";
    })(FeatureState = Season.FeatureState || (Season.FeatureState = {}));
    Season.FeatureDisplayMap = {
        [Features.BUILDERS]: "Builders",
        [Features.TRIP_SEARCH]: "Trip Search",
        [Features.CART]: "Cart",
        [Features.REGISTRATION]: "Registration",
        [Features.PAYMENT]: "Payments",
        [Features.DOCUMENTS]: "Documents",
        [Features.CHECKLIST]: "Checklist",
        [Features.CHANGE_TRIP]: "Change Trips"
    };
    Season.FeatureStateDisplayMap = {
        [FeatureState.ENABLED]: "Enabled",
        [FeatureState.DISABLED]: "Disabled",
        [FeatureState.READ_ONLY]: "Read Only"
    };
    let Dates;
    (function (Dates) {
        // 'TRIP_CANCELLATION_CUTOFF' = 'TRIP_CANCELLATION_CUTOFF',
        Dates["TRIP_SWITCH_CUTOFF"] = "TRIP_SWITCH_CUTOFF";
        // 'FIRST_PAYMENT_DEADLINE' = 'FIRST_PAYMENT_DEADLINE',
        // 'FINAL_PAYMENT_DEADLINE' = 'FINAL_PAYMENT_DEADLINE',
        Dates["LOTTERY_AGE_DATE"] = "LOTTERY_AGE_DATE";
    })(Dates = Season.Dates || (Season.Dates = {}));
    Season.DateDisplayMap = {
        // [Dates.TRIP_CANCELLATION_CUTOFF]: "Trip Cancellation Cutoff",
        [Dates.TRIP_SWITCH_CUTOFF]: "Trip Switch Cutoff",
        // [Dates.FIRST_PAYMENT_DEADLINE]: "First Payment Deadline",
        // [Dates.FINAL_PAYMENT_DEADLINE]: "Final Payment Deadline",
        [Dates.LOTTERY_AGE_DATE]: "Lottery Age Date"
    };
    function Default() {
        return {
            name: '',
            isCurrent: false,
            registrationStartDate: Timestamp.now(),
            lastChecklistSyncTime: Timestamp.now(),
            lotteryLogMessage: '',
            features: {
                [Features.BUILDERS]: FeatureState.DISABLED,
                [Features.TRIP_SEARCH]: FeatureState.DISABLED,
                [Features.CART]: FeatureState.DISABLED,
                [Features.REGISTRATION]: FeatureState.DISABLED,
                [Features.PAYMENT]: FeatureState.DISABLED,
                [Features.DOCUMENTS]: FeatureState.DISABLED,
                [Features.CHECKLIST]: FeatureState.DISABLED,
                [Features.CHANGE_TRIP]: FeatureState.DISABLED
            },
            dates: {
                // [Dates.TRIP_CANCELLATION_CUTOFF]: null,
                [Dates.TRIP_SWITCH_CUTOFF]: null,
                // [Dates.FIRST_PAYMENT_DEADLINE]: null,
                // [Dates.FINAL_PAYMENT_DEADLINE]: null,
                [Dates.LOTTERY_AGE_DATE]: null
            }
        };
    }
    Season.Default = Default;
    /**
     * Fetches all the trips in the given season that are for a the destination input
     * @param destinationId
     * @param seasonId
     * @returns
     */
    async function fetchTripsForDestination(destinationId, seasonId) {
        const subset = Season.Collection.subCollectionEntity(seasonId, "Trips").subset(q => q.where("destination", "==", destinationId));
        await subset.fetch();
        return subset.dataItems();
    }
    Season.fetchTripsForDestination = fetchTripsForDestination;
    /**
     * Fetches all the builders in the given season that are assigned to trip provided
     * @param tripId
     * @param seasonId
     * @returns
     */
    async function fetchBuildersForTrip(tripId, seasonId) {
        const subset = Season.Collection.subCollectionEntity(seasonId, "BuilderRecord").subset(q => q.where("tripAssigned", "==", tripId));
        await subset.fetch();
        return subset.dataItems();
    }
    Season.fetchBuildersForTrip = fetchBuildersForTrip;
    Season.Relationships = {
        BuilderRecord: ["BuilderRecord", BuilderRecord.CollectionClass],
        BuilderHealthRecord: ["BuilderHealthRecord", BuilderHealthRecord.CollectionClass],
        Trips: ["Trips", Trips.CollectionClass],
        ChecklistDefinitions: ["ChecklistDefinitions", ChecklistDefinitions.CollectionClass],
        ChecklistOverrides: ["ChecklistOverrides", ChecklistOverrides.CollectionClass],
        ParentTrainings: ["ParentTrainings", ParentTrainings.CollectionClass]
    };
    class CollectionClass extends CollectionEntity {
        relationships = Season.Relationships;
        defaults = Default;
        async getCurrentSeason() {
            const seasons = await this.all().fetch();
            return seasons.find(s => s.data().isCurrent)?.data();
        }
    }
    Season.CollectionClass = CollectionClass;
    Season.Collection = new CollectionClass({ path: "Seasons" });
    Season.FEATURE_DEFAULT_STATE = FeatureState.ENABLED;
    async function checkFeatureState(feature, seasonId) {
        const season = await (seasonId ? Season.Collection.fetchData(seasonId) : Season.Collection.getCurrentSeason());
        if (!season)
            throw new Error("Season not found");
        const featureState = season.features?.[feature] ?? Season.FEATURE_DEFAULT_STATE;
        return featureState;
    }
    Season.checkFeatureState = checkFeatureState;
    /** Returns true if byDate (or current date) is before the season's specified date */
    async function checkBeforeDate(date, byDate = new Date(), seasonId) {
        const season = await (seasonId ? Season.Collection.fetchData(seasonId) : Season.Collection.getCurrentSeason());
        if (!season)
            throw new Error("Season not found");
        const d = season.dates?.[date];
        if (!d)
            throw new Error(`Date not set: ${date}, on season ${season.$id}`);
        const refDate = new Date(d).getTime();
        return byDate.getTime() < refDate;
    }
    Season.checkBeforeDate = checkBeforeDate;
    /** Returns true if byDate (or current date) is after the season's specified date */
    async function checkAfterDate(date, byDate = new Date(), seasonId) {
        const season = await (seasonId ? Season.Collection.fetchData(seasonId) : Season.Collection.getCurrentSeason());
        if (!season)
            throw new Error("Season not found");
        const d = season.dates?.[date];
        if (!d)
            throw new Error(`Date not set: ${date}, on season ${season.$id}`);
        const refDate = new Date(d).getTime();
        return byDate.getTime() > refDate;
    }
    Season.checkAfterDate = checkAfterDate;
    async function getSeasonDate(date, seasonId) {
        const season = await (seasonId ? Season.Collection.fetchData(seasonId) : Season.Collection.getCurrentSeason());
        if (!season)
            throw new Error("Season not found");
        const d = season.dates?.[date];
        if (!d)
            throw new Error(`Date not set: ${date}, on season ${season.$id}`);
        return new Date(d);
    }
    Season.getSeasonDate = getSeasonDate;
    async function getBuilderZohoId(firebaseId, seasonId) {
        const item = await Season.Collection.subCollectionEntity(seasonId, "BuilderRecord").fetchData(firebaseId);
        if (item) {
            return item.zohoId || undefined;
        }
        else {
            console.warn(`Could not find builder with firebaseId ${firebaseId} in season ${seasonId}`);
            return undefined;
        }
    }
    Season.getBuilderZohoId = getBuilderZohoId;
    async function getBuilderFirestoreId(zohoId, seasonId) {
        const subset = Season.Collection.subCollectionEntity(seasonId, "BuilderRecord").subset(q => q.where("zohoId", "==", zohoId));
        await subset.fetch();
        const item = subset.size > 0 ? subset.dataItems()[0] : undefined;
        if (item) {
            return item.$id || undefined;
        }
        else {
            console.warn(`Could not find builder with zohoId ${zohoId} in season ${seasonId}`);
            return undefined;
        }
    }
    Season.getBuilderFirestoreId = getBuilderFirestoreId;
    async function getSeasonNameWithFirebaseId(firebaseId) {
        const season = await Season.Collection.fetchData(firebaseId);
        if (!season) {
            throw new Error(`Could not find season with firebaseId ${firebaseId}`);
        }
        return season.name;
    }
    Season.getSeasonNameWithFirebaseId = getSeasonNameWithFirebaseId;
    function getFirebaseId(zohoId) {
        console.log("Season uses the Zoho ID for both Firebase and Zoho");
        return Promise.resolve(zohoId);
    }
    Season.getFirebaseId = getFirebaseId;
    function getZohoId(firebaseId) {
        console.log("Season uses the Zoho ID for both Firebase and Zoho");
        return Promise.resolve(firebaseId);
    }
    Season.getZohoId = getZohoId;
    async function getFirebaseIdFromSeasonName(name) {
        const subset = Season.Collection.subset(q => q.where("name", "==", name));
        await subset.fetch();
        return subset.size > 0 ? Promise.resolve(subset.dataItems()[0].$id) : Promise.resolve(undefined);
    }
    Season.getFirebaseIdFromSeasonName = getFirebaseIdFromSeasonName;
    function getFirestoreIdFromNameOrId(nameOrId) {
        if (RE_SEASON_YEAR.test(nameOrId)) {
            return getFirebaseIdFromSeasonName(nameOrId);
        }
        else {
            return getFirebaseId(nameOrId);
        }
    }
    Season.getFirestoreIdFromNameOrId = getFirestoreIdFromNameOrId;
})(Season || (Season = {}));
//# sourceMappingURL=index.js.map